export const DOCUMENT_TYPE_OPTIONS = [
  'Driver license / ID',
  'SSN card',
  'EIN letter',
  'Tax return',
  'Bank statement',
  'Paystub',
  'Proof of address',
  'Business registration',
  'Articles of organization',
  'Operating agreement',
  'Business license',
  'Voided check',
  'Utility bill',
  'Credit report',
  'Approval screenshot',
  'Denial letter',
  'Funding application',
  'Other document',
];

export const OTHER_DOCUMENT_TYPE = 'Other document';

export function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function createUploadedDocument(file, {
  defaultTypes = [],
  normalized = null,
} = {}) {
  const types = defaultTypes.length ? [...defaultTypes] : ['Driver license / ID'];
  const source = normalized || {};
  const resolvedFile = source.file || file;
  const conversionStatus = source.conversionStatus
    || (source.convertedFromHeic ? 'converted' : 'ready');

  return {
    id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    file: resolvedFile,
    fileName: source.fileName || resolvedFile?.name || file?.name || 'document',
    fileSize: source.fileSize ?? resolvedFile?.size ?? file?.size ?? 0,
    mimeType: source.mimeType || resolvedFile?.type || file?.type || 'application/octet-stream',
    previewUrl: source.previewUrl || null,
    originalFilename: source.originalFilename || file?.name || resolvedFile?.name || null,
    convertedFilename: source.convertedFilename || null,
    originalMimeType: source.originalMimeType || file?.type || resolvedFile?.type || null,
    convertedFromHeic: Boolean(source.convertedFromHeic),
    conversionStatus,
    conversionError: source.conversionError || null,
    documentTypes: types,
    primaryDocumentType: types[0] || '',
    customName: '',
    saveToClientProfile: true,
    useForExtraction: true,
    attachToApplication: false,
    scanStatus: 'ready',
    extractionConfidence: null,
    extractedFields: [],
    notes: '',
  };
}

export function documentDisplayName(doc) {
  if (doc.documentTypes?.includes(OTHER_DOCUMENT_TYPE) && doc.customName?.trim()) {
    return doc.customName.trim();
  }
  return doc.fileName;
}

export function validateUploadedDocuments(documents) {
  const invalidOther = documents.find(
    (doc) => doc.documentTypes?.includes(OTHER_DOCUMENT_TYPE) && !doc.customName?.trim(),
  );
  if (invalidOther) {
    return 'Name required for Other document types';
  }
  if (!documents.some((doc) => doc.useForExtraction)) {
    return 'Select at least one document for extraction';
  }
  return null;
}

const UPLOAD_TYPE_TO_FUNDING_CATEGORY = {
  'Driver license / ID': 'Driver License',
  'SSN card': 'Signed Authorization',
  'EIN letter': 'EIN Letter',
  'Tax return': 'Tax Returns',
  'Bank statement': 'Bank Statements',
  'Paystub': 'Tax Returns',
  'Proof of address': 'Lease / Mortgage',
  'Business registration': 'Business Formation',
  'Articles of organization': 'Business Formation',
  'Operating agreement': 'Business Formation',
  'Business license': 'Business Formation',
  'Voided check': 'Voided Check',
  'Utility bill': 'Lease / Mortgage',
  'Credit report': 'Credit Report',
  'Approval screenshot': 'Loan Statements',
  'Denial letter': 'Loan Statements',
  'Funding application': 'Signed Authorization',
  'Other document': 'Signed Authorization',
};

export function mapUploadTypeToFundingCategory(documentType) {
  return UPLOAD_TYPE_TO_FUNDING_CATEGORY[documentType] || 'Signed Authorization';
}

export function uploadedDocumentToFundingPayload(doc) {
  const primaryType = doc.primaryDocumentType || doc.documentTypes?.[0] || 'Other document';
  const notesParts = [];
  if (doc.customName?.trim()) notesParts.push(doc.customName.trim());
  if (doc.notes?.trim()) notesParts.push(doc.notes.trim());
  if (doc.convertedFromHeic && doc.originalFilename) {
    notesParts.push(`Converted from HEIC: ${doc.originalFilename}`);
  }

  return {
    category: mapUploadTypeToFundingCategory(primaryType),
    status: 'Uploaded',
    file_name: doc.fileName,
    notes: notesParts.length ? notesParts.join(' · ') : undefined,
    original_filename: doc.originalFilename || undefined,
    converted_filename: doc.convertedFilename || undefined,
    original_mime_type: doc.originalMimeType || undefined,
    mime_type: doc.mimeType || undefined,
    converted_from_heic: Boolean(doc.convertedFromHeic) || undefined,
  };
}
